import { FormDialogMode, useNotify } from '@globaleris/ui.library'
import { Grid } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumTipoAdhesionCFlotante, EnumTipoConceptoFlotante } from '../../../api'
import {
  FloatingConceptsDataTableModel,
  FloatingConceptsModel
} from '../../../api/Argentia/endpoints/floatingConcepts/model/floatingConcepts'
import { SettlementGroup } from '../../../api/Argentia/endpoints/settlementGroups/model/settlementGroups'
import { formatDateOnlyString } from '../../../helpers/formatDateOnly'
import { getResourceUserPermissions } from '../../../helpers/permissionsHelper'
import { useConfirmationDialog, usePageState } from '../../../hooks'
import { useDropdownOptions } from '../../../hooks/useDropdownOptions'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { Resources } from '../../../model/RBAC/constants'
import { mapSettlementGroupOptionsList } from '../../settlementGroups/settlementGroups.mapper'
import { getSettlementGroupsWithFlexibilities } from '../../settlementGroups/settlementGroups.service'
import {
  mapFloatingConceptFormDataToPatchRequest,
  mapFloatingConceptsDataTableToState
} from '../floatingConcepts.mapper'
import { EnumFloatingConceptsTabs } from '../floatingConcepts.model'
import {
  deleteFloatingConcept,
  fetchBalanceActionsFloatingConcepts,
  fetchFloatingConceptsBySettlementGroupID,
  patchFloatingConcept
} from '../floatingConcepts.service'
import { FloatingConceptsDataTable } from './floatingConceptsDataTable/floatingConcepts.DataTable'
import { FloatingConceptsFormDialog } from './floatingConceptsEditFormDialog/floatingConceptsFormDialog'
import { FloatingConceptsSearchForm } from './searchForm/floatingConcepts.searchForm'

type FloatingConceptsBalanceActionsTabProps = {}

interface FormDependenciesProps {
  formState: any
  amountError: boolean
}

export const FloatingConceptsBalanceActionsTab: React.FC<
  FloatingConceptsBalanceActionsTabProps
> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'floatingConcepts', 'settlementGroups'])
  const history = useHistory()
  const { dataTableFilter, dataTableSearch } = usePageState()
  const { info, error } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const {
    isLoading: isLoadingSettlementGroupsOptions,
    responseData: settlementGroupData,
    responseError: settlementGroupDataError,
    fetchData: fetchSettlementGroupsOptions
  } = useDropdownOptions<SettlementGroup, Parameters<typeof getSettlementGroupsWithFlexibilities>>(
    getSettlementGroupsWithFlexibilities
  )

  const [formDialogOpen, setFormDialogOpen] = useState<boolean>(false)
  const [formMode, setFormMode] = useState<FormDialogMode>(FormDialogMode.ADD)
  const [formInputData, setFormInputData] = useState<FloatingConceptsDataTableModel>(
    {} as FloatingConceptsDataTableModel
  )
  const [floatingConcepts, setFloatingConcepts] = useState<Partial<FloatingConceptsModel>[]>([])
  const [floatingConceptsDataTable, setFloatingConceptsDataTable] = useState<
    FloatingConceptsDataTableModel[]
  >([])
  const [searchForm, setSearchForm] = useState<FloatingConceptsSearchForm>(
    {} as FloatingConceptsSearchForm
  )
  const [formHasValue, setFormHasValue] = useState<boolean>(false)
  const [isFetchingTable, setIsFetchingTable] = useState<boolean>(false)
  const [last1000FilterIsActive, setLast1000FilterIsActive] = useState<boolean>(false)

  // #region Handlers Starts

  // ------------------- DataTable Action Handlers -----------------------------//

  const onAddFloatingConceptClickHandler = async (
    formData: FloatingConceptsDataTableModel | undefined
  ) => {
    history.push(NonAuthRoutes.floatingConceptsAdd, {
      activeTab: EnumFloatingConceptsTabs.balanceActions,
      settlementGroupOptionsList: mapSettlementGroupOptionsList(settlementGroupData),
      floatingConceptType: EnumTipoConceptoFlotante.AccionBalance,
      adhesionType: EnumTipoAdhesionCFlotante.AccionBalance
    })
  }

  const onEditFloatingConceptClickHandler = async (conceptData: any) => {
    setFormMode(FormDialogMode.EDIT)
    setFormDialogOpen(true)
    setFormInputData(conceptData)
  }

  const onDeleteFloatingConceptClickHandler = (
    rowFloatingConcept: FloatingConceptsDataTableModel
  ) => {
    getConfirmation(ConfirmationDialogs.DELETE_PRICEVALUE).then(async () => {
      deleteFloatingConcept(rowFloatingConcept.conceptId!).then(async response => {
        if (response) {
          updateFloatingConceptsTable()
          info(t('floatingConcepts:notifications.deleteFloatingConcept.success'))
        } else {
          error(t('floatingConcepts:notifications.deleteFloatingConcept.error'))
        }
      })
      closeConfirmation()
    })
  }

  // ------------------- EditForm Dialog Handlers -----------------------------//

  const handleFormDialogClose = (formData: any) => {
    if (formData) {
      if (formMode === FormDialogMode.EDIT) {
        const request = mapFloatingConceptFormDataToPatchRequest(formData)
        patchFloatingConcept(request).then(async response => {
          if (response.success) {
            updateFloatingConceptsTable()
            info(t('floatingConcepts:notifications.editFloatingConcept.success'))
          } else {
            error(t('floatingConcepts:notifications.editFloatingConcept.error'))
            return
          }
        })
      }
    }
    setFormDialogOpen(false)
  }

  // ------------------- SearchForm Handlers -----------------------------//

  const handleSearchAllButton = async (searchFormData: FloatingConceptsSearchForm) => {
    setLast1000FilterIsActive(false)
    setIsFetchingTable(true)
    setSearchForm(searchFormData)
    await fetchFloatingConceptsBySettlementGroupID(
      searchFormData.settlementGroupId!,
      formatDateOnlyString(searchFormData.startDate),
      formatDateOnlyString(searchFormData.endDate)
    )
      .then(response => {
        if (response.success && response.data) {
          setFloatingConcepts(mapFloatingConceptsDataTableToState(response.data)) // TableModel omits expanded related entities like Contract or SettlementGroup (except for related entities Id) to improve performance
          setFloatingConceptsDataTable(response.data)
        } else {
          error(t('floatingConcepts:notifications.loadFloatingConcepts.error'))
        }
      })
      .finally(() => {
        setIsFetchingTable(false)
      })
  }

  const handleFilterLastButton = () => {
    setLast1000FilterIsActive(true)
    updateFloatingConceptsTable()
  }

  const handleFormClear = () => {
    setFormHasValue(false)
    setFloatingConceptsDataTable([])
  }

  const handleFormHasValue = (hasValue: boolean) => {
    setFormHasValue(hasValue)
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  const updateFloatingConceptsTable = async () => {
    setIsFetchingTable(true)
    await fetchBalanceActionsFloatingConcepts()
      .then(response => {
        if (response.success && response.data) {
          setFloatingConcepts(mapFloatingConceptsDataTableToState(response.data)) // TableModel omits expanded related entities like Contract or SettlementGroup (except for related entities Id) to improve performance
          setFloatingConceptsDataTable(response.data)
        } else {
          error(t('floatingConcepts:notifications.loadFloatingConcepts.error'))
        }
      })
      .finally(() => {
        setIsFetchingTable(false)
      })
  }

  useEffect(() => {
    fetchSettlementGroupsOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // useEffect(() => {
  //   if (settlementGroupDataError) {
  //     error(t('floatingConcepts:notifications.loadSettlementGroups.error'))
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [settlementGroupDataError])

  // #endregion Hooks Ends

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FloatingConceptsSearchForm
            activeTab={EnumFloatingConceptsTabs.balanceActions}
            onFormClear={handleFormClear}
            onFormHasValue={handleFormHasValue}
            onSearchAllButton={handleSearchAllButton}
            onFilterLastButton={handleFilterLastButton}
            settlementGroupOptionsList={mapSettlementGroupOptionsList(settlementGroupData)}
            isLoadingSettlementGroupsOptions={isLoadingSettlementGroupsOptions}
          />
        </Grid>
        <Grid item xs={12}>
          <FloatingConceptsDataTable
            activeTab={EnumFloatingConceptsTabs.balanceActions}
            settlementGroupOptionsList={mapSettlementGroupOptionsList(settlementGroupData)}
            isFetchingTable={isFetchingTable}
            floatingConceptsDataTable={floatingConceptsDataTable}
            userPermissions={getResourceUserPermissions(Resources.BalanceActions)}
            dataTableFilter={dataTableFilter}
            dataTableSearch={dataTableSearch}
            onAddFloatingConceptClick={onAddFloatingConceptClickHandler}
            onAddDisabled={isLoadingSettlementGroupsOptions}
            onEditFloatingConceptClick={onEditFloatingConceptClickHandler}
            onDeleteFloatingConceptClick={onDeleteFloatingConceptClickHandler}
          />
        </Grid>
      </Grid>
      <FloatingConceptsFormDialog
        activeTab={EnumFloatingConceptsTabs.balanceActions}
        formMode={formMode}
        open={formDialogOpen}
        onClose={handleFormDialogClose}
        inputData={formInputData}
        settlementGroupOptionsList={mapSettlementGroupOptionsList(settlementGroupData)}
      />
    </>
  )
}
